// risata.ts — dal volto alla risata: logica pura, usata dall'app su ogni fotogramma analizzato.
//
// MediaPipe Face Landmarker dà per ogni fotogramma dei "blendshape" (valori 0–1: quanto sorride la bocca a
// sinistra, quanto è aperta la mascella…). Qui si trasforma la sequenza in eventi:
//   - "risata": sorriso E bocca aperta (medie smorzate) sopra i loro minimi, punteggio sopra la soglia, per almeno
//     N fotogrammi E almeno `durataMinimaMs` di fila. Chi sorride soltanto (anche tanto), chi fa una smorfia o
//     parla NON ride: la partita finisce solo con una risata vera;
//   - "volto": presente / assente (senza volto per più di `voltoAssenteMs`).
// Un sorriso trattenuto NON è una risata: serve anche la bocca aperta (o le guance contratte).
// Tutti i numeri arrivano dalle impostazioni app.rilevamento.* (regolabili dal pannello, si provano sui telefoni veri).

export interface ParametriRilevamento {
  soglia: number; // 0–1: punteggio oltre il quale "sta ridendo"
  fotogrammi: number; // fotogrammi consecutivi sopra soglia per dire "ha riso"
  smorzamento: number; // 0–1: peso del fotogramma nuovo nella media (1 = nessuno smorzamento)
  pesoSorriso: number;
  pesoBocca: number;
  pesoGuance: number;
  sorrisoMinimo: number; // senza almeno questo sorriso non è mai una risata (bocca aperta per parlare)
  boccaMinima: number; // senza almeno questa apertura della bocca non è mai una risata (sorriso a bocca chiusa)
  durataMinimaMs: number; // la risata deve durare almeno così (una smorfia veloce non conta)
  refrattarioMs: number; // dopo una risata non se ne segnala un'altra prima di questo tempo
  voltoAssenteMs: number;
}

/** Nomi dei blendshape di MediaPipe usati (gli altri si ignorano). */
export const BLENDSHAPE = {
  sorrisoSx: "mouthSmileLeft",
  sorrisoDx: "mouthSmileRight",
  mascella: "jawOpen",
  guanciaSx: "cheekSquintLeft",
  guanciaDx: "cheekSquintRight",
} as const;

export type Blendshape = Readonly<Record<string, number>>;

/** Media (0–1) dei blendshape indicati, con i valori sporchi trattati come 0. */
function valore(b: Blendshape, ...chiavi: string[]): number {
  let somma = 0;
  for (const k of chiavi) {
    const x = b[k];
    somma += typeof x === "number" && Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0;
  }
  return somma / chiavi.length;
}

/** Punteggio istantaneo 0–1 di un fotogramma. */
export function punteggioRisata(b: Blendshape, p: ParametriRilevamento): number {
  const v = (k: string) => {
    const x = b[k];
    return typeof x === "number" && Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0;
  };
  const sorriso = (v(BLENDSHAPE.sorrisoSx) + v(BLENDSHAPE.sorrisoDx)) / 2;
  if (sorriso < p.sorrisoMinimo) return 0;
  const bocca = v(BLENDSHAPE.mascella);
  const guance = (v(BLENDSHAPE.guanciaSx) + v(BLENDSHAPE.guanciaDx)) / 2;
  const somma = p.pesoSorriso + p.pesoBocca + p.pesoGuance;
  if (somma <= 0) return 0;
  return (p.pesoSorriso * sorriso + p.pesoBocca * bocca + p.pesoGuance * guance) / somma;
}

export type EventoRilevamento = { tipo: "risata"; t: number; punteggio: number } | { tipo: "volto"; presente: boolean; t: number };

/** Segue un volto (il proprio o quello dell'avversario) fotogramma per fotogramma. */
export class RilevatoreRisata {
  private media = 0;
  private mediaSorriso = 0;
  private mediaBocca = 0;
  private sopra = 0;
  private sopraDa = 0;
  private ultimaRisata = -Infinity;
  private ultimoVolto: number | null = null;
  private voltoPresente = true;
  private p: ParametriRilevamento;

  constructor(p: ParametriRilevamento) {
    this.p = p;
  }

  aggiornaParametri(p: ParametriRilevamento): void {
    this.p = p;
  }

  get punteggioAttuale(): number {
    return this.media;
  }

  /** Riparte da zero (nuovo round): niente risate "in coda" dal round prima. */
  azzera(t: number): void {
    this.media = 0;
    this.mediaSorriso = 0;
    this.mediaBocca = 0;
    this.sopra = 0;
    this.ultimaRisata = -Infinity;
    this.ultimoVolto = t;
  }

  /** Un fotogramma analizzato all'ora `t` (ms); `b` null = nessun volto trovato. */
  fotogramma(t: number, b: Blendshape | null): EventoRilevamento[] {
    const eventi: EventoRilevamento[] = [];
    if (b === null) {
      this.sopra = 0;
      this.media *= 1 - this.p.smorzamento;
      this.mediaSorriso *= 1 - this.p.smorzamento;
      this.mediaBocca *= 1 - this.p.smorzamento;
      if (this.ultimoVolto === null) this.ultimoVolto = t;
      if (this.voltoPresente && t - this.ultimoVolto >= this.p.voltoAssenteMs) {
        this.voltoPresente = false;
        eventi.push({ tipo: "volto", presente: false, t });
      }
      return eventi;
    }
    this.ultimoVolto = t;
    if (!this.voltoPresente) {
      this.voltoPresente = true;
      eventi.push({ tipo: "volto", presente: true, t });
    }
    const s = punteggioRisata(b, this.p);
    const k = this.p.smorzamento;
    this.media = k * s + (1 - k) * this.media;
    // Sorriso e bocca si seguono anche da soli (smorzati): ridendo la bocca si apre e chiude, la media la tiene aperta.
    this.mediaSorriso = k * valore(b, BLENDSHAPE.sorrisoSx, BLENDSHAPE.sorrisoDx) + (1 - k) * this.mediaSorriso;
    this.mediaBocca = k * valore(b, BLENDSHAPE.mascella) + (1 - k) * this.mediaBocca;
    // Le medie smorzate restano alte per un po' anche dopo che il volto si è rilassato: per contare la DURATA serve
    // che anche il fotogramma di adesso sorrida davvero (una smorfia veloce non si "allunga" con la media).
    const sorrisoOra = valore(b, BLENDSHAPE.sorrisoSx, BLENDSHAPE.sorrisoDx);
    const ride =
      sorrisoOra >= this.p.sorrisoMinimo &&
      this.media >= this.p.soglia &&
      this.mediaSorriso >= this.p.sorrisoMinimo &&
      this.mediaBocca >= this.p.boccaMinima;
    if (ride) {
      if (this.sopra === 0) this.sopraDa = t;
      this.sopra++;
    } else this.sopra = 0;
    if (this.sopra >= this.p.fotogrammi && t - this.sopraDa >= this.p.durataMinimaMs && t - this.ultimaRisata >= this.p.refrattarioMs) {
      this.ultimaRisata = t;
      this.sopra = 0;
      eventi.push({ tipo: "risata", t, punteggio: Math.round(this.media * 100) / 100 });
    }
    return eventi;
  }
}
